/**
 * SourceInfoPage
 *
 * Displays source details including connection info, authentication status,
 * documentation (guide.md), and metadata. View-only.
 */

import * as React from 'react'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useAtom } from 'jotai'
import { AlertCircle, ExternalLink, RefreshCw, FileText, Check, Circle, ArrowDown, ArrowUp, AlertTriangle, Trash2, Plus, X, MoreHorizontal, Paperclip, ChevronDown, File as FileIcon } from 'lucide-react'
import { EditPopover, EditButton, getEditConfig } from '@/components/ui/EditPopover'
import { SourceAvatar } from '@/components/ui/source-avatar'
import { SourceMenu } from '@/components/app-shell/SourceMenu'
import { DeleteKnowledgeDialog } from '@/components/app-shell/DeleteKnowledgeDialog'
import { DropdownMenu, DropdownMenuTrigger, StyledDropdownMenuContent, StyledDropdownMenuItem, StyledDropdownMenuSeparator } from '@/components/ui/styled-dropdown'
import { usePlatform } from '@craft-agent/ui'
import { isIPad } from '@/lib/platform'
import { cn } from '@/lib/utils'
import { routes, navigate } from '@/lib/navigate'
import { useNavigation } from '@/contexts/NavigationContext'
import { toast } from 'sonner'
import {
  Info_Page,
  Info_Section,
  Info_Table,
  Info_Alert,
  Info_Markdown,
  PermissionsDataTable,
  ToolsDataTable,
  type PermissionRow,
  type ToolRow,
} from '@/components/info'
import { useKnowledgeSync } from '@/hooks/useKnowledgeSync'
import { pendingEditCollectionSlugAtom } from '@/atoms/sources'
import type { LoadedSource, McpToolWithPermission } from '@/shared/types'
import type { PermissionsConfigFile } from '@craft-agent/shared/agent/modes'
import { isApiOAuthProvider } from '@craft-agent/shared/sources/types'
import { isSourceUsable } from '@craft-agent/shared/sources'
import { SourceStatusIndicator, deriveConnectionStatus } from '@/components/ui/source-status-indicator'

interface SourceInfoPageProps {
  sourceSlug: string
  workspaceId: string
  /** Optional callback when source is deleted */
  onDelete?: () => void
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp?: number): string {
  if (!timestamp) return 'Never'

  const now = Date.now()
  const diff = now - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  return `${days} day${days !== 1 ? 's' : ''} ago`
}

/**
 * Get source URL for display
 */
function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getSourceUrl(source: LoadedSource): string | null {
  const { type, mcp, api, local } = source.config

  if (type === 'mcp' && mcp?.url) return mcp.url
  if (type === 'api' && api?.baseUrl) return api.baseUrl
  if (type === 'local' && local?.path) return local.path

  return null
}

/**
 * Normalize a Loom URL to its embeddable form.
 * Converts share links (loom.com/share/xxx) to embed links (loom.com/embed/xxx).
 * Passes through non-Loom URLs unchanged.
 */
function toLoomEmbedUrl(url: string): string {
  return url.replace('loom.com/share/', 'loom.com/embed/')
}

/**
 * Convert permissions config to PermissionRow[] for API/local sources
 */
function buildApiPermissionsData(config: PermissionsConfigFile): PermissionRow[] {
  const rows: PermissionRow[] = []

  // Blocked Tools
  config.blockedTools?.forEach((item) => {
    const pattern = typeof item === 'string' ? item : item.pattern
    const comment = typeof item === 'string' ? null : item.comment
    rows.push({ access: 'blocked', type: 'tool', pattern, comment })
  })

  // Allowed Bash Patterns
  config.allowedBashPatterns?.forEach((item) => {
    const pattern = typeof item === 'string' ? item : item.pattern
    const comment = typeof item === 'string' ? null : item.comment
    rows.push({ access: 'allowed', type: 'bash', pattern, comment })
  })

  // Allowed API Endpoints
  config.allowedApiEndpoints?.forEach((item) => {
    const pattern = `${item.method} ${item.path}`
    const comment = typeof item === 'object' && 'comment' in item ? item.comment : null
    rows.push({ access: 'allowed', type: 'api', pattern, comment })
  })

  return rows
}

/**
 * Convert permissions config to PermissionRow[] for MCP sources
 */
function buildMcpPermissionsData(config: PermissionsConfigFile): PermissionRow[] {
  const rows: PermissionRow[] = []

  // Blocked Tools
  config.blockedTools?.forEach((item) => {
    const pattern = typeof item === 'string' ? item : item.pattern
    const comment = typeof item === 'string' ? null : item.comment
    rows.push({ access: 'blocked', type: 'mcp', pattern, comment })
  })

  // Allowed MCP Patterns
  config.allowedMcpPatterns?.forEach((item) => {
    const pattern = typeof item === 'string' ? item : item.pattern
    const comment = typeof item === 'string' ? null : item.comment
    rows.push({ access: 'allowed', type: 'mcp', pattern, comment })
  })

  return rows
}

/**
 * Convert MCP tools to ToolRow[]
 */
function buildToolsData(tools: McpToolWithPermission[]): ToolRow[] {
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description || '',
    permission: tool.allowed ? 'allowed' : 'requires-permission',
  }))
}

/**
 * Get contextual description for Connection section based on source type
 */
function getConnectionDescription(source: LoadedSource): string {
  const { type, mcp } = source.config

  if (type === 'mcp') {
    if (mcp?.transport === 'stdio') {
      return 'Local command that spawns this MCP server.'
    }
    return 'Server URL and connection status.'
  }
  if (type === 'api') {
    return 'Base URL for API requests.'
  }
  if (type === 'local') {
    return 'Filesystem path for this source.'
  }
  return 'Connection details.'
}

/**
 * Get contextual description for Permissions section based on source type
 */
function getPermissionsDescription(source: LoadedSource): string {
  const { type } = source.config

  if (type === 'mcp') {
    return 'Tool patterns allowed in Explore mode.'
  }
  if (type === 'api') {
    return 'API endpoints allowed in Explore mode.'
  }
  return 'Access rules for Explore mode.'
}

function getToolsDescription(source: LoadedSource): string {
  if (source.config.slug === 'devin') {
    return 'Operations exposed by this server. Use the DeepWiki tools (`ask_question`, `list_available_repos`, `read_wiki_*`) and avoid org-scoped session/integration tools shown here, because Devin still advertises them even though PAT and Service User tokens do not get the required `X-Org-Id` forwarded by the MCP server.'
  }

  return 'Operations exposed by this server.'
}

export default function SourceInfoPage({ sourceSlug, workspaceId, onDelete }: SourceInfoPageProps) {
  const { navigateToSource } = useNavigation()
  const { onOpenFile } = usePlatform()
  const [source, setSource] = useState<LoadedSource | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [permissionsConfig, setPermissionsConfig] = useState<PermissionsConfigFile | null>(null)
  const [mcpTools, setMcpTools] = useState<McpToolWithPermission[] | null>(null)
  const [mcpToolsLoading, setMcpToolsLoading] = useState(false)
  const [mcpToolsError, setMcpToolsError] = useState<string | null>(null)
  const [localMcpEnabled, setLocalMcpEnabled] = useState(true)

  const isKnowledge = source?.config.type === 'knowledge'
  const knowledge = useKnowledgeSync(workspaceId, sourceSlug, isKnowledge)
  const [collectionDetails, setCollectionDetails] = useState<{ long_description: string | null; description: string | null; name: string | null; icon: string | null; updatedAt: string | null; is_protected?: boolean } | null>(null)
  const [aboutEditorOpen, setAboutEditorOpen] = useState(false)
  const aboutWrittenRef = useRef(false)
  const [isEditingHero, setIsEditingHero] = useState(false)
  const [editName, setEditName] = useState('')
  const [editTagline, setEditTagline] = useState('')
  const [editIcon, setEditIcon] = useState('')
  const [isSavingHero, setIsSavingHero] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [credentialInput, setCredentialInput] = useState('')
  const [isSavingCredential, setIsSavingCredential] = useState(false)
  const [isInstallingBridge, setIsInstallingBridge] = useState(false)
  const [pendingDeleteSlug, setPendingDeleteSlug] = useState<string | null>(null)
  const [showNewItemInput, setShowNewItemInput] = useState(false)
  const [newItemTitle, setNewItemTitle] = useState('')
  const [expandedItemSlug, setExpandedItemSlug] = useState<string | null>(null)
  const [pendingDeleteFileId, setPendingDeleteFileId] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Load source data
  useEffect(() => {
    let isMounted = true
    setSource(null)
    setLoading(true)
    setError(null)

    const loadSource = async () => {
      try {
        const sources = await window.electronAPI.getSources(workspaceId)

        if (!isMounted) return

        const found = sources.find((s: LoadedSource) => s.config.slug === sourceSlug)
        if (found) {
          setSource(found)

          const config = await window.electronAPI.getSourcePermissionsConfig(workspaceId, sourceSlug)
          if (isMounted) {
            setPermissionsConfig(config)
          }
        } else {
          setError('Source not found')
        }
      } catch (err) {
        if (!isMounted) return
        setError(err instanceof Error ? err.message : 'Failed to load source')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadSource()

    return () => {
      isMounted = false
    }
  }, [workspaceId, sourceSlug])

  // Load MCP tools when source is loaded and is MCP type
  useEffect(() => {
    if (!source || source.config.type !== 'mcp') {
      setMcpTools(null)
      setMcpToolsError(null)
      return
    }

    let isMounted = true
    setMcpToolsLoading(true)
    setMcpToolsError(null)

    const loadTools = async () => {
      try {
        const result = await window.electronAPI.getMcpTools(workspaceId, sourceSlug)
        if (!isMounted) return

        if (result.success && result.tools) {
          setMcpTools(result.tools)
        } else {
          setMcpToolsError(result.error || 'Failed to load tools')
        }
      } catch (err) {
        if (!isMounted) return
        setMcpToolsError(err instanceof Error ? err.message : 'Failed to load tools')
      } finally {
        if (isMounted) setMcpToolsLoading(false)
      }
    }

    loadTools()

    return () => {
      isMounted = false
    }
  }, [source, workspaceId, sourceSlug])

  // Load collection details for knowledge sources
  useEffect(() => {
    if (!isKnowledge || !source?.config.knowledge?.collectionId) return
    let isMounted = true
    window.electronAPI.knowledgeGetCollection(source.config.knowledge.collectionId)
      .then((collection: any) => {
        if (isMounted && collection) {
          setCollectionDetails({ long_description: collection.long_description, description: collection.description, name: collection.name, icon: collection.icon, updatedAt: collection.updated_at ?? null, is_protected: collection.is_protected })
        }
      })
      .catch(() => {})
    return () => { isMounted = false }
  }, [isKnowledge, source?.config.knowledge?.collectionId])

  // Load workspace settings (for localMcpEnabled)
  useEffect(() => {
    if (!workspaceId) return
    window.electronAPI.getWorkspaceSettings(workspaceId).then((settings: Record<string, any> | null) => {
      if (settings) {
        setLocalMcpEnabled(settings.localMcpEnabled ?? true)
      }
    }).catch((err: unknown) => {
      console.error('[SourceInfoPage] Failed to load workspace settings:', err)
    })
  }, [workspaceId])

  // Listen for source folder changes
  useEffect(() => {
    if (!window.electronAPI?.onSourcesChanged) return

    const cleanup = window.electronAPI.onSourcesChanged((changedWorkspaceId: string, sources: LoadedSource[]) => {
      if (changedWorkspaceId !== workspaceId) return
      const updated = sources.find((s: LoadedSource) => s.config.slug === sourceSlug)

      if (updated) {
        setSource(updated)

        const loadPermissionsConfig = async () => {
          try {
            const config = await window.electronAPI.getSourcePermissionsConfig(workspaceId, sourceSlug)
            setPermissionsConfig(config)
          } catch (err) {
            console.error('[SourceInfoPage] Failed to reload permissions config:', err)
          }
        }
        loadPermissionsConfig()
      }
    })

    return cleanup
  }, [sourceSlug, workspaceId])

  // Write .about.md when collection details are loaded (so EditPopover agent can edit it)
  useEffect(() => {
    if (!isKnowledge || collectionDetails === null) {
      aboutWrittenRef.current = false
      return
    }
    window.electronAPI.knowledgeWriteAbout(workspaceId, sourceSlug, collectionDetails.long_description ?? '')
      .then(() => { aboutWrittenRef.current = true })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- collectionDetails is intentionally excluded; only long_description field matters
  }, [isKnowledge, collectionDetails?.long_description, workspaceId, sourceSlug])

  // Auto-push .about.md to server when EditPopover closes
  useEffect(() => {
    if (aboutEditorOpen || !aboutWrittenRef.current || !source?.config.knowledge?.collectionId) return
    const timer = setTimeout(async () => {
      const result = await window.electronAPI.knowledgePushAbout(
        workspaceId, sourceSlug, source.config.knowledge!.collectionId
      )
      if (result.success) {
        const collection = await window.electronAPI.knowledgeGetCollection(source.config.knowledge!.collectionId)
        if (collection) {
          setCollectionDetails({ long_description: collection.long_description, description: collection.description, name: collection.name, icon: collection.icon, updatedAt: collection.updated_at ?? null })
        }
        toast.success('About section updated')
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [aboutEditorOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCollectionDetails = useCallback(async () => {
    if (!source?.config.knowledge?.collectionId) return
    try {
      const collection = await window.electronAPI.knowledgeGetCollection(source.config.knowledge.collectionId)
      if (collection) {
        setCollectionDetails({ long_description: collection.long_description, description: collection.description, name: collection.name, icon: collection.icon, updatedAt: collection.updated_at ?? null })
      }
    } catch {}
  }, [source?.config.knowledge?.collectionId])

  const [pendingEditSlug, setPendingEditSlug] = useAtom(pendingEditCollectionSlugAtom)

  const handleStartEditHero = useCallback(() => {
    setEditName(source?.config.name || '')
    setEditTagline(source?.config.tagline || collectionDetails?.description || '')
    setEditIcon(source?.config.icon || '')
    setIsEditingHero(true)
  }, [source?.config.name, source?.config.tagline, source?.config.icon, collectionDetails?.description])

  // Auto-trigger edit mode when navigated from sidebar "Edit Collection"
  useEffect(() => {
    if (pendingEditSlug === sourceSlug && isKnowledge && source) {
      setPendingEditSlug(null)
      handleStartEditHero()
    }
  }, [pendingEditSlug, sourceSlug, isKnowledge, source, setPendingEditSlug, handleStartEditHero])

  const handleSaveHero = useCallback(async () => {
    if (!source?.config.knowledge?.collectionId) return
    setIsSavingHero(true)
    try {
      const fields: { name?: string; description?: string; icon?: string } = {}
      if (editName !== (source.config.name || '')) fields.name = editName
      if (editTagline !== (source.config.tagline || collectionDetails?.description || '')) fields.description = editTagline
      if (editIcon !== (source.config.icon || '')) fields.icon = editIcon

      if (Object.keys(fields).length === 0) {
        setIsEditingHero(false)
        return
      }

      const result = await window.electronAPI.knowledgeUpdateCollection(
        source.config.knowledge.collectionId,
        workspaceId,
        sourceSlug,
        fields
      )
      if (result.success) {
        await refreshCollectionDetails()
        toast.success('Collection updated')
        setIsEditingHero(false)
      } else {
        toast.error('Failed to update collection', { description: result.error })
      }
    } catch (err) {
      toast.error('Failed to update collection', { description: err instanceof Error ? err.message : 'Unknown error' })
    } finally {
      setIsSavingHero(false)
    }
  }, [source?.config.knowledge?.collectionId, source?.config.name, source?.config.tagline, source?.config.icon, editName, editTagline, editIcon, collectionDetails?.description, workspaceId, sourceSlug, refreshCollectionDetails])

  // Compute source URL
  const sourceUrl = useMemo(() => source ? getSourceUrl(source) : null, [source])

  // Build data for PermissionsDataTable
  const apiPermissionsData = useMemo(() => {
    if (!permissionsConfig || source?.config.type === 'mcp') return []
    return buildApiPermissionsData(permissionsConfig)
  }, [permissionsConfig, source])

  const mcpPermissionsData = useMemo(() => {
    if (!permissionsConfig || source?.config.type !== 'mcp') return []
    return buildMcpPermissionsData(permissionsConfig)
  }, [permissionsConfig, source])

  // Build data for ToolsDataTable
  const toolsData = useMemo(() => {
    if (!mcpTools) return []
    return buildToolsData(mcpTools)
  }, [mcpTools])

  // Handle opening URL (website or folder)
  const handleOpenUrl = useCallback(async () => {
    if (!source || !sourceUrl) return
    if (sourceUrl.startsWith('http://') || sourceUrl.startsWith('https://')) {
      if (isIPad) {
        window.open(sourceUrl, '_blank', 'noopener,noreferrer')
      } else {
        await window.electronAPI.openUrl(sourceUrl)
      }
    } else if (!isIPad) {
      await window.electronAPI.showInFolder(sourceUrl)
    }
  }, [source, sourceUrl])

  // Handle opening source folder
  const handleOpenSourceFolder = useCallback(async () => {
    if (!source) return
    if (window.electronAPI) {
      await window.electronAPI.showInFolder(source.folderPath)
    }
  }, [source])

  // Handle deleting source (navigates to source list, preserving current filter)
  const handleDelete = useCallback(async () => {
    if (!source) return
    if (isKnowledge) {
      setShowDeleteDialog(true)
      return
    }
    try {
      await window.electronAPI.deleteSource(workspaceId, sourceSlug)
      toast.success(`Deleted source: ${source.config.name}`)
      navigateToSource() // Navigate to source list, preserving filter
      onDelete?.()
    } catch (err) {
      toast.error('Failed to delete source', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [source, isKnowledge, workspaceId, sourceSlug, onDelete, navigateToSource])

  const handleConfirmDelete = useCallback(async () => {
    if (!source) return
    try {
      await window.electronAPI.deleteSource(workspaceId, sourceSlug)
      toast.success(`Deleted source: ${source.config.name}`)
      navigateToSource()
      onDelete?.()
    } catch (err) {
      toast.error('Failed to delete source', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [source, workspaceId, sourceSlug, onDelete, navigateToSource])

  // Handle opening in new window
  const handleOpenInNewWindow = useCallback(() => {
    window.electronAPI.openUrl(`originrelay://sources/source/${sourceSlug}?window=focused`)
  }, [sourceSlug])

  // Check if source supports OAuth authentication
  const supportsOAuth = useMemo(() => {
    if (!source) return false
    if (source.config.type === 'api' && isApiOAuthProvider(source.config.provider)) return true
    if (source.config.type === 'mcp' && source.config.mcp?.authType === 'oauth') return true
    return false
  }, [source])

  // Check if source requires a bearer token / credential
  const requiresToken = useMemo(() => {
    if (!source) return false
    // MCP bearer sources (exclude sources with bundled bearer tokens — they work automatically via env vars)
    if (source.config.type === 'mcp' && source.config.mcp?.authType === 'bearer' && !source.config.mcp.bundledBearerEnv) return true
    // API sources with non-OAuth auth (bearer, header, query, basic)
    // Exclude sources with bundled API keys (they work automatically via env vars)
    if (
      source.config.type === 'api' &&
      source.config.api?.authType &&
      source.config.api.authType !== 'none' &&
      !isApiOAuthProvider(source.config.provider) &&
      !source.config.api.bundledApiKeyEnv
    ) return true
    return false
  }, [source])

  // Detect auth-related errors from MCP tool loading (e.g. expired OAuth session)
  const hasAuthError = useMemo(() => {
    if (!mcpToolsError) return false
    const lower = mcpToolsError.toLowerCase()
    return lower.includes('session expired') ||
      lower.includes('sign in again') ||
      lower.includes('unauthorized') ||
      lower.includes('401') ||
      lower.includes('authentication required') ||
      lower.includes('token expired') ||
      lower.includes('invalid token') ||
      lower.includes('access denied')
  }, [mcpToolsError])

  const needsAuth = source != null && (
    (!isSourceUsable(source) || hasAuthError) && (supportsOAuth || requiresToken)
  )

  // Determine auth mode: 'oauth' for OAuth flows, 'token' for bearer/credential input
  const authMode: 'oauth' | 'token' = requiresToken ? 'token' : 'oauth'

  // Handle OAuth authentication from source page
  const handleAuthenticate = useCallback(async () => {
    if (!source) return
    setIsAuthenticating(true)
    try {
      const result = await window.electronAPI.performOAuth({
        sourceSlug,
        provider: source.config.provider,
      })
      if (result.success) {
        toast.success(`Connected to ${source.config.name}`)
      } else {
        toast.error('Authentication failed', { description: result.error })
      }
    } catch (err) {
      toast.error('Authentication failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsAuthenticating(false)
    }
  }, [source, sourceSlug])

  // Handle saving bearer token / credential
  const handleSaveCredential = useCallback(async () => {
    if (!source || !credentialInput.trim()) return
    setIsSavingCredential(true)
    try {
      await window.electronAPI.saveSourceCredentials(workspaceId, sourceSlug, credentialInput.trim())
      toast.success(`Credentials saved for ${source.config.name}`)
      setCredentialInput('')
    } catch (err) {
      toast.error('Failed to save credentials', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsSavingCredential(false)
    }
  }, [source, credentialInput, workspaceId, sourceSlug])

  const handleInstallFigmaBridge = useCallback(async () => {
    setIsInstallingBridge(true)
    try {
      const result = await window.electronAPI.installFigmaDesktopBridge()
      toast.success('Figma Desktop Bridge installed', {
        description: `Plugin copied to ${result.destinationPath}. Open Figma → Plugins → Development → Import plugin from manifest → select manifest.json from that folder.`,
      })
    } catch (err) {
      toast.error('Failed to install Figma Desktop Bridge', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsInstallingBridge(false)
    }
  }, [])

  // Get source name for header
  const sourceName = source?.config.name || sourceSlug

  return (
    <>
    <Info_Page
      loading={loading}
      error={error ?? undefined}
      empty={!source && !loading && !error ? 'Source not found' : undefined}
    >
      <Info_Page.Header
        title={sourceName}
        titleMenu={
          <SourceMenu
            sourceSlug={sourceSlug}
            sourceName={sourceName}
            isDefault={source?.config.isDefault}
            isProtectedKnowledge={isKnowledge && (source?.config.knowledge?.isProtected === true || collectionDetails?.is_protected === true)}
            onEditCollection={isKnowledge ? handleStartEditHero : undefined}
            onOpenInNewWindow={handleOpenInNewWindow}
            onShowInFinder={handleOpenSourceFolder}
            onDelete={handleDelete}
          />
        }
      />

      {source && (
        <Info_Page.Content>
          {/* Hero: Avatar, title, and tagline */}
          {isKnowledge && isEditingHero ? (
            <div className="flex flex-col gap-3 px-6 py-5">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={editIcon}
                  onChange={(e) => setEditIcon(e.target.value)}
                  placeholder="Icon (emoji or URL)"
                  className="w-16 text-center text-2xl bg-transparent border border-border/50 rounded-md px-2 py-1 outline-none focus:border-foreground/30"
                />
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Collection name"
                  className="flex-1 text-lg font-semibold bg-transparent border border-border/50 rounded-md px-3 py-1.5 outline-none focus:border-foreground/30"
                />
              </div>
              <input
                type="text"
                value={editTagline}
                onChange={(e) => setEditTagline(e.target.value)}
                placeholder="Description"
                className="text-sm text-muted-foreground bg-transparent border border-border/50 rounded-md px-3 py-1.5 outline-none focus:border-foreground/30"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveHero}
                  disabled={isSavingHero}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSavingHero ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditingHero(false)}
                  disabled={isSavingHero}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <Info_Page.Hero
              avatar={<SourceAvatar source={source} fluid />}
              title={source.config.name}
              badge={<SourceStatusIndicator status={deriveConnectionStatus(source, localMcpEnabled)} size="sm" />}
              tagline={source.config.tagline || (isKnowledge ? collectionDetails?.description : undefined) || undefined}
            />
          )}

          {/* Disabled Warning */}
          {source.config.mcp?.transport === 'stdio' && !localMcpEnabled && (
            <Info_Alert variant="warning" icon={<AlertCircle className="h-4 w-4" />}>
              <Info_Alert.Title>Source Disabled</Info_Alert.Title>
              <Info_Alert.Description>
                Local MCP servers are disabled in Settings &gt; Advanced.
                Enable them to use this source.
              </Info_Alert.Description>
            </Info_Alert>
          )}

          {/* Authentication Required */}
          {needsAuth && (
            <Info_Alert variant="warning" icon={<AlertCircle className="h-4 w-4" />}>
              <Info_Alert.Title>{hasAuthError ? 'Session Expired' : 'Authentication Required'}</Info_Alert.Title>
              {authMode === 'oauth' ? (
                <div className="flex items-center justify-between gap-3 mt-1">
                  <span className="text-foreground/60 text-sm">
                    {hasAuthError
                      ? `Your ${source.config.name} session has expired. Please reconnect.`
                      : `Connect your account to start using ${source.config.name}.`}
                  </span>
                  <button
                    onClick={handleAuthenticate}
                    disabled={isAuthenticating}
                    className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {isAuthenticating ? (
                      <>
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Connecting…
                      </>
                    ) : (
                      hasAuthError ? 'Reconnect' : 'Connect'
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 mt-1">
                  <span className="text-foreground/60 text-sm">
                    {source.config.mcp?.transport === 'stdio'
                      ? `Enter your connection string to start using ${source.config.name}.`
                      : `Enter your API token to start using ${source.config.name}.`}
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="password"
                      value={credentialInput}
                      onChange={(e) => setCredentialInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCredential() }}
                      placeholder={source.config.mcp?.transport === 'stdio' ? 'Connection string…' : 'API token…'}
                      className="flex-1 text-sm bg-transparent border border-border/50 rounded-md px-3 py-1.5 outline-none focus:border-foreground/30 placeholder:text-muted-foreground/50"
                    />
                    <button
                      onClick={handleSaveCredential}
                      disabled={isSavingCredential || !credentialInput.trim()}
                      className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {isSavingCredential ? (
                        <>
                          <RefreshCw className="h-3 w-3 animate-spin" />
                          Saving…
                        </>
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </Info_Alert>
          )}

          {/* Update Token — shown for authenticated token-based sources so user can re-enter */}
          {!needsAuth && requiresToken && source != null && isSourceUsable(source) && (
            <Info_Section title="API Token" description="Update your access token if needed.">
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={credentialInput}
                  onChange={(e) => setCredentialInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCredential() }}
                  placeholder="Paste new token…"
                  className="flex-1 text-sm bg-transparent border border-border/50 rounded-md px-3 py-1.5 outline-none focus:border-foreground/30 placeholder:text-muted-foreground/50"
                />
                <button
                  onClick={handleSaveCredential}
                  disabled={isSavingCredential || !credentialInput.trim()}
                  className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSavingCredential ? (
                    <>
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </Info_Section>
          )}

          {!isIPad && source.config.slug === 'figma-console' && (
            <Info_Section title="Desktop Bridge Plugin" description="Required for write access — creating designs, editing, and managing variables in Figma.">
              <div className="flex items-center justify-between px-5 py-2.5">
                <p className="text-xs text-muted-foreground">
                  Installs the plugin and copies it to your Desktop for easy import into Figma.
                </p>
                <button
                  onClick={handleInstallFigmaBridge}
                  disabled={isInstallingBridge}
                  className="inline-flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isInstallingBridge ? (
                    <>
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Installing…
                    </>
                  ) : (
                    'Install Desktop Bridge'
                  )}
                </button>
              </div>
            </Info_Section>
          )}

          {/* About - for knowledge sources (always shown, with empty state) */}
          {isKnowledge && (
            <Info_Section
              title="About"
              description="Overview and guidelines for this collection."
              actions={
                <div className="flex items-center gap-1.5">
                  {collectionDetails?.long_description && (
                    <button
                      onClick={refreshCollectionDetails}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      title="Re-fetch from server"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Fetch latest
                    </button>
                  )}
                  <EditPopover
                    trigger={<EditButton />}
                    {...getEditConfig('knowledge-about', source.folderPath)}
                    open={aboutEditorOpen}
                    onOpenChange={setAboutEditorOpen}
                  />
                </div>
              }
            >
              {collectionDetails?.long_description ? (
                <>
                  <Info_Markdown maxHeight={540} fullscreen>
                    {collectionDetails.long_description}
                  </Info_Markdown>
                  {collectionDetails.updatedAt && (
                    <div className="px-4 py-1.5 text-right">
                      <span className="text-xs text-muted-foreground">
                        Updated {formatRelativeTime(new Date(collectionDetails.updatedAt).getTime())}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center rounded-lg border border-dashed py-6 text-xs text-muted-foreground">
                  No about section yet. Click the edit button to add one.
                </div>
              )}
            </Info_Section>
          )}

          {/* Knowledge Items - for knowledge sources */}
          {isKnowledge && (
            <Info_Section
              title="Knowledge Items"
              description={knowledge.lastSyncedAt
                ? `Last synced ${formatRelativeTime(new Date(knowledge.lastSyncedAt).getTime())}`
                : 'Pull to sync knowledge items.'}
              actions={
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setShowNewItemInput(true)}
                    disabled={knowledge.isSyncing}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New
                  </button>
                  <button
                    onClick={knowledge.refresh}
                    disabled={knowledge.isSyncing}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                    title="Check for updates"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh
                  </button>
                  <button
                    onClick={knowledge.pull}
                    disabled={knowledge.isSyncing}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                      knowledge.updateStatus?.hasRemoteUpdates
                        ? 'text-blue-600 bg-blue-500/10 hover:bg-blue-500/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )}
                  >
                    <ArrowDown className={cn('h-3.5 w-3.5', knowledge.isSyncing && 'animate-bounce')} />
                    Pull{knowledge.updateStatus?.remoteUpdateCount ? ` (${knowledge.updateStatus.remoteUpdateCount})` : ''}
                  </button>
                  <button
                    onClick={knowledge.pushAll}
                    disabled={knowledge.isSyncing || !knowledge.updateStatus?.hasLocalChanges}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                      knowledge.updateStatus?.hasLocalChanges
                        ? 'text-amber-600 bg-amber-500/10 hover:bg-amber-500/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                    Push{knowledge.updateStatus?.localChangeCount ? ` (${knowledge.updateStatus.localChangeCount})` : ''}
                  </button>
                </div>
              }
            >
              {knowledge.error && (
                <div className="px-4 py-2 border-b border-border/30 bg-destructive/5">
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{knowledge.error}</span>
                  </div>
                </div>
              )}
              {showNewItemInput && (
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-accent/30">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input
                    autoFocus={!isIPad}
                    type="text"
                    placeholder="Item title..."
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && newItemTitle.trim()) {
                        const slug = await knowledge.createItem(newItemTitle.trim())
                        setNewItemTitle('')
                        setShowNewItemInput(false)
                        if (slug) {
                          onOpenFile?.(`${source.folderPath}/items/${slug}/content.md`)
                        }
                      }
                      if (e.key === 'Escape') {
                        setNewItemTitle('')
                        setShowNewItemInput(false)
                      }
                    }}
                    className="flex-1 text-sm bg-transparent border-none outline-none placeholder:text-muted-foreground/50"
                  />
                  <button
                    onClick={() => {
                      setNewItemTitle('')
                      setShowNewItemInput(false)
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {knowledge.items.length === 0 && !knowledge.error && !showNewItemInput && (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No knowledge items synced yet. Click <strong>Pull</strong> to download items from the server.
                </div>
              )}
              {knowledge.items.length > 0 && (
                <div className="divide-y divide-border/30">
                  {knowledge.items.map((item) => (
                    <React.Fragment key={item.slug}>
                    <div
                      className="group flex items-center gap-3 w-full px-4 py-2.5 hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => onOpenFile?.(`${source.folderPath}/items/${item.slug}/content.md`)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.title}</div>
                          <div className="text-xs text-muted-foreground">
                            Updated {formatRelativeTime(new Date(item.updatedAt).getTime())}
                          </div>
                        </div>
                      </button>
                      {item.syncStatus === 'conflicted' && (
                        <span title="Conflict — open to resolve"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-500" /></span>
                      )}
                      {item.syncStatus === 'synced' && (
                        <span title="Synced — up to date with server"><Check className="h-3.5 w-3.5 shrink-0 text-green-500" /></span>
                      )}
                      {item.syncStatus === 'modified' && (
                        <span title="Modified locally — push to share changes"><Circle className="h-3.5 w-3.5 shrink-0 text-amber-500 fill-amber-500" /></span>
                      )}
                      {item.syncStatus === 'new' && (
                        <span title="New — push to upload to server"><ArrowUp className="h-3.5 w-3.5 shrink-0 text-blue-500" /></span>
                      )}
                      {item.syncStatus === 'remote-updated' && (
                        <span title="Update available — pull to download"><ArrowDown className="h-3.5 w-3.5 shrink-0 text-blue-500" /></span>
                      )}
                      {((item.fileCount ?? 0) > 0 || (knowledge.files[item.slug]?.length ?? 0) > 0) && (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                          title={`${knowledge.files[item.slug]?.length ?? item.fileCount} attached file(s)`}
                        >
                          <Paperclip className="h-3 w-3" />
                          {knowledge.files[item.slug]?.length ?? item.fileCount}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          const newSlug = expandedItemSlug === item.slug ? null : item.slug
                          setExpandedItemSlug(newSlug)
                          if (newSlug) knowledge.listFiles(item.slug)
                        }}
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        title="Toggle files"
                      >
                        <ChevronDown className={cn(
                          'h-3.5 w-3.5 transition-transform',
                          expandedItemSlug === item.slug && 'rotate-180'
                        )} />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <StyledDropdownMenuContent align="end" sideOffset={4}>
                          <StyledDropdownMenuItem onClick={() => onOpenFile?.(`${source.folderPath}/items/${item.slug}/content.md`)}>
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span>Open</span>
                          </StyledDropdownMenuItem>
                          <StyledDropdownMenuItem onClick={() => knowledge.uploadFile(item.slug)}>
                            <Paperclip className="h-3.5 w-3.5" />
                            <span>Attach File</span>
                          </StyledDropdownMenuItem>
                          <StyledDropdownMenuSeparator />
                          <StyledDropdownMenuItem
                            variant="destructive"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPendingDeleteSlug(item.slug)
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span>Delete</span>
                          </StyledDropdownMenuItem>
                        </StyledDropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {expandedItemSlug === item.slug && (
                      <div className="pl-11 pr-4 py-2 bg-accent/20 border-b border-border/30">
                        {(knowledge.files[item.slug] ?? []).length === 0 ? (
                          <div className="text-xs text-muted-foreground py-1">No files attached.</div>
                        ) : (
                          <div className="space-y-1">
                            {knowledge.files[item.slug].map((file) => (
                              <div key={file.id} className="flex items-center gap-2 text-xs group/file">
                                <FileIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                <span className="flex-1 truncate">{file.filename}</span>
                                <span className="text-muted-foreground shrink-0">
                                  {formatFileSize(file.size_bytes)}
                                </span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="p-0.5 rounded text-muted-foreground hover:text-foreground opacity-0 group-hover/file:opacity-100 transition-opacity">
                                      <MoreHorizontal className="h-3 w-3" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <StyledDropdownMenuContent align="end" sideOffset={4}>
                                    <StyledDropdownMenuItem onClick={() => knowledge.replaceFile(file.id, item.slug, file.filename)}>
                                      <RefreshCw className="h-3.5 w-3.5" />
                                      <span>Replace</span>
                                    </StyledDropdownMenuItem>
                                    <StyledDropdownMenuSeparator />
                                    <StyledDropdownMenuItem
                                      variant="destructive"
                                      onClick={() => setPendingDeleteFileId(file.id)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                      <span>Delete</span>
                                    </StyledDropdownMenuItem>
                                  </StyledDropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        )}
                        {pendingDeleteFileId && knowledge.files[item.slug]?.some(f => f.id === pendingDeleteFileId) && (
                          <div className="mt-2 px-2 py-2 rounded bg-destructive/5 border border-destructive/20">
                            <div className="text-xs mb-1.5">Delete this file?</div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const fileToDelete = knowledge.files[item.slug]?.find(f => f.id === pendingDeleteFileId)
                                  if (fileToDelete) {
                                    knowledge.deleteFile(pendingDeleteFileId, item.slug, fileToDelete.filename)
                                  }
                                  setPendingDeleteFileId(null)
                                }}
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setPendingDeleteFileId(null)}
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => knowledge.uploadFile(item.slug)}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          Attach file
                        </button>
                      </div>
                    )}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {pendingDeleteSlug && (
                <div className="px-4 py-3 border-t border-border/30 bg-destructive/5">
                  <div className="text-sm mb-2">Delete this item locally and from the server?</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        knowledge.deleteItem(pendingDeleteSlug)
                        setPendingDeleteSlug(null)
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setPendingDeleteSlug(null)}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </Info_Section>
          )}

          {/* Connection/Permissions/Tools/Documentation - for non-knowledge sources */}
          {!isKnowledge && (
            <>
              {/* How to Use — tutorial content (default sources only) */}
              {source.tutorial && (
                <Info_Section
                  title="How to Use"
                  description={`Learn how to get the most out of ${source.config.name}.`}
                >
                  {source.tutorial.videoUrl && (
                    <div className="px-6 pt-4">
                      <iframe
                        src={toLoomEmbedUrl(source.tutorial.videoUrl)}
                        className="w-full aspect-video rounded-lg"
                        sandbox="allow-scripts allow-same-origin"
                        allowFullScreen
                        allow="autoplay; fullscreen"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <Info_Markdown maxHeight={540} fullscreen>
                    {source.tutorial.content}
                  </Info_Markdown>
                </Info_Section>
              )}

              {/* Connection */}
              <Info_Section
                title="Connection"
                description={getConnectionDescription(source)}
                actions={
                  <EditPopover
                    trigger={<EditButton />}
                    {...getEditConfig('source-config', source.folderPath)}
                    secondaryAction={{
                      label: 'Edit File',
                      filePath: `${source.folderPath}/config.json`,
                    }}
                  />
                }
              >
                <Info_Table
                  footer={source.config.connectionError && (
                    <div className="px-4 py-2 border-t border-border/30 bg-destructive/5">
                      <div className="flex items-start gap-2 text-sm text-destructive">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{source.config.connectionError}</span>
                      </div>
                    </div>
                  )}
                >
                  <Info_Table.Row label="Type" value={source.config.type.toUpperCase()} />
                  {sourceUrl && (
                    <Info_Table.Row label="URL">
                      <button
                        onClick={handleOpenUrl}
                        className="truncate hover:underline text-foreground focus:outline-none focus-visible:underline text-left block w-full"
                      >
                        {sourceUrl}
                      </button>
                    </Info_Table.Row>
                  )}
                  <Info_Table.Row label="Last Tested" value={formatRelativeTime(source.config.lastTestedAt)} />
                </Info_Table>
              </Info_Section>

              {/* Permissions - for API and local sources */}
              {source.config.type !== 'mcp' && permissionsConfig && apiPermissionsData.length > 0 && (
                <Info_Section
                  title="Permissions"
                  description={getPermissionsDescription(source)}
                  actions={
                    <EditPopover
                      trigger={<EditButton />}
                      {...getEditConfig('source-permissions', source.folderPath)}
                      secondaryAction={{
                        label: 'Edit File',
                        filePath: `${source.folderPath}/permissions.json`,
                      }}
                    />
                  }
                >
                  <PermissionsDataTable data={apiPermissionsData} fullscreen fullscreenTitle="Permissions" />
                </Info_Section>
              )}

              {/* Tools - for MCP sources */}
              {source.config.type === 'mcp' && (
                <Info_Section
                  title="Tools"
                  description={getToolsDescription(source)}
                  actions={
                    <EditPopover
                      trigger={<EditButton />}
                      {...getEditConfig('source-tool-permissions', source.folderPath)}
                      secondaryAction={{
                        label: 'Edit File',
                        filePath: `${source.folderPath}/permissions.json`,
                      }}
                    />
                  }
                >
                  <ToolsDataTable
                    data={toolsData}
                    loading={mcpToolsLoading}
                    error={mcpToolsError ?? undefined}
                  />
                </Info_Section>
              )}

              {/* Permissions - for MCP sources */}
              {source.config.type === 'mcp' && permissionsConfig && mcpPermissionsData.length > 0 && (
                <Info_Section
                  title="Permissions"
                  description={getPermissionsDescription(source)}
                  actions={
                    <EditPopover
                      trigger={<EditButton />}
                      {...getEditConfig('source-permissions', source.folderPath)}
                      secondaryAction={{
                        label: 'Edit File',
                        filePath: `${source.folderPath}/permissions.json`,
                      }}
                    />
                  }
                >
                  <PermissionsDataTable data={mcpPermissionsData} hideTypeColumn fullscreen fullscreenTitle="Permissions" />
                </Info_Section>
              )}

              {/* Documentation */}
              {source.guide?.raw && (
                <Info_Section
                  title="Documentation"
                  description="Context and guidelines for the agent."
                  actions={
                    <EditPopover
                      trigger={<EditButton />}
                      {...getEditConfig('source-guide', source.folderPath)}
                      secondaryAction={{
                        label: 'Edit File',
                        filePath: `${source.folderPath}/guide.md`,
                      }}
                    />
                  }
                >
                  <Info_Markdown maxHeight={540} fullscreen>
                    {source.guide.raw}
                  </Info_Markdown>
                </Info_Section>
              )}
            </>
          )}
        </Info_Page.Content>
      )}
    </Info_Page>

    <DeleteKnowledgeDialog
      open={showDeleteDialog}
      onOpenChange={setShowDeleteDialog}
      collectionName={source?.config.name ?? 'this collection'}
      onConfirm={handleConfirmDelete}
    />
  </>
  )
}
